import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowRight, ArrowDownRight } from "lucide-react";

const LINES = [
  <>STRONG BOXES</>,
  <>
    FOR <span className="text-mint">KAMPALA&rsquo;S</span>
  </>,
  <>FINEST BRANDS</>,
];

const EASE = [0.16, 1, 0.3, 1];

function Cube() {
  const rx = useSpring(useMotionValue(0), { stiffness: 60, damping: 14 });
  const ry = useSpring(useMotionValue(0), { stiffness: 60, damping: 14 });
  const ref = useRef(null);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 26);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 26);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { rx.set(0); ry.set(0); }} className="cube-scene" data-testid="hero-cube">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="flex items-center justify-center">
        <div className="cube">
          {["cf-front", "cf-back", "cf-right", "cf-left", "cf-top", "cf-bottom"].map((f) => (
            <div key={f} className={`cube-face ${f}`}>
              {f === "cf-front" && (
                <span className="relative z-10 font-mono text-[10px] tracking-[0.3em] text-mintsoft/90 bg-forest/60 px-2 py-1">
                  MUBFAR
                </span>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default function Hero() {
  const { scrollY } = useScroll();
  const yGlow = useTransform(scrollY, [0, 600], [0, 120]);
  const yType = useTransform(scrollY, [0, 600], [0, 60]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* parallax glow + grid */}
      <motion.div style={{ y: yGlow }} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[55vw] h-[55vw] rounded-full bg-pine/25 blur-[130px]" />
        <div className="absolute bottom-[-30%] left-[-15%] w-[40vw] h-[40vw] rounded-full bg-gold/10 blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.13]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(110,231,183,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(110,231,183,0.35) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(ellipse 90% 70% at 50% 30%, black 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 90% 70% at 50% 30%, black 30%, transparent 75%)",
          }}
        />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-6 items-center pt-32 pb-20 lg:pt-24">
        <motion.div style={{ y: yType }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-mintsoft/90 mb-6"
            data-testid="hero-eyebrow"
          >
            MUBFAR — Custom Box Makers · Kampala, UG
          </motion.p>

          <h1 className="font-display font-semibold tracking-tight leading-[0.95] text-[13.5vw] sm:text-6xl lg:text-7xl xl:text-[5.4rem] text-kraft">
            {LINES.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base sm:text-lg text-kraft/70 leading-relaxed"
          >
            Gift, pizza, cake, chalk and burger boxes, juice holders, round boxes for
            takeaway, paper bags, packaging and corrugated boxes — cut, printed and folded to your
            exact size. No listed prices: tell us what you need, we quote fast.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-cta-shop"
              className="group inline-flex items-center gap-2 rounded-full bg-mint text-forest font-semibold px-7 h-12 hover:bg-kraft transition-colors"
            >
              Browse the shop
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/quote"
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-2 rounded-full border border-mint/40 text-kraft px-7 h-12 hover:border-mint hover:text-mint transition-colors"
            >
              Start a quote
              <ArrowDownRight size={17} />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] tracking-[0.25em] text-mintsoft/70 uppercase"
          >
            <span>RETAIL + BULK</span>
            <span className="text-mint">◆</span>
            <span>CUSTOM PRINT</span>
            <span className="text-mint">◆</span>
            <span>KAMPALA DELIVERY</span>
          </motion.div>
        </motion.div>

        {/* 3D moment */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
          className="lg:col-span-5 relative"
        >
          <div className="relative flex flex-col items-center">
            <div
              className="absolute inset-0 rounded-full"
              style={{ background: "radial-gradient(circle at 50% 55%, rgba(16,185,129,0.28), transparent 62%)" }}
            />
            <Cube />
            <div className="mt-2 h-6 w-40 rounded-[100%] bg-black/50 blur-md" />
            <div className="mt-8 w-full max-w-xs space-y-4 font-mono text-[10px] tracking-[0.2em] text-mintsoft/80">
              <p className="text-center uppercase" data-testid="cube-caption">
                LIVE PREVIEW — EVERY BOX CUT TO YOUR SIZE
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
